import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
    Tabs,
    Tab
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getAssignedInfluencers, getCompletedInfluencers, updateInfluencerStatus } from '../services/api';
import { ContentCopy, Email, Visibility, Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function AdminInfluencers({ user }) {
    const [tabValue, setTabValue] = useState(0);
    const [assignedInfluencers, setAssignedInfluencers] = useState([]);
    const [completedInfluencers, setCompletedInfluencers] = useState([]);
    const [inProgressInfluencers, setInProgressInfluencers] = useState([]);
    const [notInterestedInfluencers, setNotInterestedInfluencers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
    const [assignedSelectedRows, setAssignedSelectedRows] = useState([]);
    const [completedSelectedRows, setCompletedSelectedRows] = useState([]);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [selectedInfluencerId, setSelectedInfluencerId] = useState('');
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusRemark, setStatusRemark] = useState('');
    const [currentInfluencerId, setCurrentInfluencerId] = useState('');
    const [currentInfluencerRemarks, setCurrentInfluencerRemarks] = useState([]);
    const [remarksModalOpen, setRemarksModalOpen] = useState(false);
    const [remarksModalData, setRemarksModalData] = useState([]);
    const [remarksModalName, setRemarksModalName] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [mobileFilter, setMobileFilter] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllInfluencers();
    }, []);

    const fetchAllInfluencers = async () => {
        setLoading(true);
        try {
            const [assignedRes, completedRes] = await Promise.all([
                getAssignedInfluencers(),
                getCompletedInfluencers()
            ]);

            setAssignedInfluencers(assignedRes.data?.data || []);
            setCompletedInfluencers(completedRes.data?.data || []);

            await fetchInProgressInfluencers();
            await fetchNotInterestedInfluencers();
        } catch (error) {
            console.error('Fetch error:', error);
            showAlert('Failed to fetch influencers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchInProgressInfluencers = async () => {
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/existing-influencers/in-progress');
            setInProgressInfluencers(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch in-progress influencers:', error);
        }
    };

    const fetchNotInterestedInfluencers = async () => {
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/existing-influencers/not-interested');
            setNotInterestedInfluencers(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch not-interested influencers:', error);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updateInfluencerStatus(id, newStatus);
            showAlert(`Status updated to ${newStatus}`, 'success');
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            const { default: api } = await import('../services/api');
            await api.put(`/existing-influencers/${id}/status`, { status: status });
            showAlert(`Status updated to ${status.replace('_', ' ')}`, 'success');
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const handleStatusClick = (id, currentStatus, remarks) => {
        setCurrentInfluencerId(id);
        setSelectedStatus(currentStatus);
        setStatusRemark('');
        setCurrentInfluencerRemarks(remarks || []);
        setStatusModalOpen(true);
    };

    const handleStatusModalUpdate = async () => {
        try {
            const { default: api } = await import('../services/api');
            await api.put(`/existing-influencers/${currentInfluencerId}/status`, {
                status: selectedStatus,
                remark: statusRemark
            });
            showAlert(`Status updated to ${selectedStatus.replace('_', ' ')}`, 'success');
            setStatusModalOpen(false);
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'assigned': return 'primary';
            case 'in_progress': return 'warning';
            case 'not_interested': return 'error';
            case 'completed': return 'success';
            default: return 'default';
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
    };

    const handleCopyUrl = (rowId) => {
        const url = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/${rowId}`;
        navigator.clipboard.writeText(url);
        toast.success('URL copied to clipboard!');
    };

    const handleSendEmailClick = (rowId) => {
        setSelectedInfluencerId(rowId);
        setEmailAddress('');
        setEmailModalOpen(true);
    };

    const handleSendEmail = async () => {
        if (!emailAddress.trim()) {
            toast.error('Please enter an email address');
            return;
        }
        setEmailSending(true);
        try {
            const { default: api } = await import('../services/api');
            const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/${selectedInfluencerId}`;
            await api.post('/send-form-email', { email: emailAddress, formUrl: formUrl });
            setEmailModalOpen(false);
            setEmailAddress('');
            setSelectedInfluencerId('');
            toast.success('Email sent successfully!');
        } catch (error) {
            toast.error('Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    const handleViewForm = (influencerFormId) => {
        if (influencerFormId) {
            window.open(`/influencer/${influencerFormId}`, '_blank');
        }
    };

    const handleExportToExcel = () => {
        let rows, sheetName;
        switch (tabValue) {
            case 0: rows = getAssignedRows(); sheetName = 'Assigned'; break;
            case 1: rows = getInProgressRows(); sheetName = 'In Progress'; break;
            case 2: rows = getNotInterestedRows(); sheetName = 'Not Interested'; break;
            case 3: rows = getCompletedRows(); sheetName = 'Completed'; break;
            default: rows = []; sheetName = 'Data';
        }

        const data = rows.map(row => ({
            'S.No': row.sno,
            'Username': row.username || '-',
            'Business Email': row.businessEmail || '-',
            'Name': row.name || '-',
            'Mobile': row.mobile || '-',
            'Profile Link': row.profileLink || '-',
            'Market (Language)': row.market || '-',
            'State': row.state || '-',
            'Assigned To': row.assignedTo || '-',
            'Given By': row.givenBy || '-',
            'Remark': (() => {
                const r = row.remark;
                if (!r) return '-';
                if (typeof r === 'string') return r;
                if (Array.isArray(r) && r.length) return r.map(item => item.text).join(' | ');
                return '-';
            })(),
            'Modified At': formatDate(row.modifiedAt),
            'Status': row.status || '-',
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${sheetName}_Influencers_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Data exported successfully!');
    };

    const filterByDate = (inf) => {
        if (!fromDate && !toDate) return true;
        if (!inf.updatedAt) return false;
        const updatedDate = dayjs(inf.updatedAt);
        if (fromDate && updatedDate.isBefore(dayjs(fromDate), 'day')) return false;
        if (toDate && updatedDate.isAfter(dayjs(toDate), 'day')) return false;
        return true;
    };

    const formatDate = (value) => {
        if (!value || value === '-') return '-';
        const date = new Date(value);
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const getUniqueLanguages = () => {
        const allInfluencers = [...assignedInfluencers, ...completedInfluencers, ...inProgressInfluencers, ...notInterestedInfluencers];
        const languages = allInfluencers
            .map(inf => inf.market)
            .filter(market => market && market !== null)
            .filter((value, index, self) => self.indexOf(value) === index);
        return languages;
    };

    const getUniqueStates = () => {
        const allInfluencers = [...assignedInfluencers, ...completedInfluencers, ...inProgressInfluencers, ...notInterestedInfluencers];
        const states = allInfluencers
            .map(inf => inf.state)
            .filter(state => state && state !== null)
            .filter((value, index, self) => self.indexOf(value) === index);
        return states;
    };

    const getAssignedRows = () => {
        return assignedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'assigned',
                modifiedAt: inf.updatedAt || '-',
            }));
    };

    const getCompletedRows = () => {
        return completedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                influencerCode: inf.influencerFormId?.influencerCode || '-',
                email: inf.influencerFormId?.email || inf.businessEmail || '-',
                name: inf.influencerFormId?.name || inf.name || '-',
                modifiedAt: inf.updatedAt || '-',
            }));
    };

    const getInProgressRows = () => {
        return inProgressInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'in_progress',
                modifiedAt: inf.updatedAt || '-',
            }));
    };

    const getNotInterestedRows = () => {
        return notInterestedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                remark: inf.remark || '-',
                businessEmail: inf.businessEmail || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'not_interested',
                modifiedAt: inf.updatedAt || '-',
            }));
    };

    const modifiedAtColumn = {
        field: 'modifiedAt',
        headerName: 'Modified At',
        width: 150,
        valueFormatter: (params) => {
            if (!params.value || params.value === '-') return '-';
            const date = new Date(params.value);
            const day = date.getDate();
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
        },
    };

    const assignedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        modifiedAtColumn,
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'assigned'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status, params.row.remark)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 200,
            renderCell: (params) => {
                const remark = params.row?.remark;
                if (!remark || remark == '-') return '-';
                if (typeof remark === 'string') return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{remark}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData([{ text: remark }]); setRemarksModalOpen(true); }} />
                    </Box>
                );
                if (!remark.length) return '-';
                const latest = remark[remark.length - 1];
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{latest?.text || '-'}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData(remark); setRemarksModalOpen(true); }} />
                    </Box>
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopyUrl(params.row.id)}
                    >
                        Copy
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleSendEmailClick(params.row.id)}
                    > Email
                    </Button>
                </Box>
            ),
        },
    ];

    const inProgressColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        modifiedAtColumn,
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'in_progress'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status, params.row.remark)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 200,
            renderCell: (params) => {
                const remark = params.row?.remark;
                if (!remark || remark == '-') return '-';
                if (typeof remark === 'string') return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{remark}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData([{ text: remark }]); setRemarksModalOpen(true); }} />
                    </Box>
                );
                if (!remark.length) return '-';
                const latest = remark[remark.length - 1];
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{latest?.text || '-'}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData(remark); setRemarksModalOpen(true); }} />
                    </Box>
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopyUrl(params.row.id)}
                    >
                        Copy
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleSendEmailClick(params.row.id)}
                    >
                        Email
                    </Button>
                </Box>
            ),
        },
    ];

    const notInterestedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        modifiedAtColumn,
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'not_interested'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status, params.row.remark)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 200,
            renderCell: (params) => {
                const remark = params.row?.remark;
                if (!remark || remark == '-') return '-';
                if (typeof remark === 'string') return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{remark}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData([{ text: remark }]); setRemarksModalOpen(true); }} />
                    </Box>
                );
                if (!remark.length) return '-';
                const latest = remark[remark.length - 1];
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{latest?.text || '-'}</Box> */}
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData(remark); setRemarksModalOpen(true); }} />
                    </Box>
                );
            }
        },
    ];

    const completedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'influencerCode', headerName: 'Influencer Code', width: 140 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        modifiedAtColumn,
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label="Completed"
                    color="success"
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status, params.row.remark)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 200,
            renderCell: (params) => {
                const remark = params.row?.remark;
                if (!remark || remark == '-') return '-';
                if (typeof remark === 'string') return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData([{ text: remark }]); setRemarksModalOpen(true); }} />
                    </Box>
                );
                if (!remark.length) return '-';
                const latest = remark[remark.length - 1];
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData(remark); setRemarksModalOpen(true); }} />
                    </Box>
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/influencer/${params?.row?.influencerFormId?._id || params?.row?.influencerFormId}`)}
                    disabled={!params.row.influencerFormId}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                My Influencers
            </Typography>

            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'nowrap', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Market</InputLabel>
                            <Select
                                value={languageFilter}
                                label="Filter by Market"
                                onChange={(e) => setLanguageFilter(e.target.value)}
                            >
                                <MenuItem value="">All Languages</MenuItem>
                                {getUniqueLanguages().map((language) => (
                                    <MenuItem key={language} value={language}>
                                        {language}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by State</InputLabel>
                            <Select
                                value={stateFilter}
                                label="Filter by State"
                                onChange={(e) => setStateFilter(e.target.value)}
                            >
                                <MenuItem value="">All States</MenuItem>
                                {getUniqueStates().map((state) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Mobile</InputLabel>
                            <Select
                                value={mobileFilter}
                                label="Filter by Mobile"
                                onChange={(e) => setMobileFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="with">With Mobile</MenuItem>
                                <MenuItem value="without">Without Mobile</MenuItem>
                            </Select>
                        </FormControl>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } }, popper: { sx: { '& .MuiPaper-root': { transform: 'scale(0.85)', transformOrigin: 'top right' } } } }}
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } }, popper: { sx: { '& .MuiPaper-root': { transform: 'scale(0.85)', transformOrigin: 'top right' } } } }}
                            />
                        </LocalizationProvider>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                            <Tab label={`Assigned (${getAssignedRows().length})`} />
                            <Tab label={`In Progress (${getInProgressRows().length})`} />
                            <Tab label={`Not Interested (${getNotInterestedRows().length})`} />
                            <Tab label={`Completed (${getCompletedRows().length})`} />
                        </Tabs>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleExportToExcel}
                        >
                            Export to Excel
                        </Button>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <DataGrid
                            rows={getAssignedRows()}
                            columns={assignedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <DataGrid
                            rows={getInProgressRows()}
                            columns={inProgressColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <DataGrid
                            rows={getNotInterestedRows()}
                            columns={notInterestedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <DataGrid
                            rows={getCompletedRows()}
                            columns={completedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>
                </CardContent>
            </Card>

            <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send Form URL via Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailModalOpen(false)} disabled={emailSending}>Cancel</Button>
                    <Button onClick={handleSendEmail} variant="contained" disabled={emailSending} sx={{ bgcolor: '#019383' }}>
                        {emailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <MenuItem value="assigned">Assigned</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="not_interested">Not Interested</MenuItem>
                        </Select>
                    </FormControl>

                    {/* {currentInfluencerRemarks && (
                        Array.isArray(currentInfluencerRemarks) && currentInfluencerRemarks.length > 0 ? (
                            <Box sx={{ mt: 2, mb: 1, maxHeight: 150, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Previous Remarks:</Typography>
                                {currentInfluencerRemarks.map((r, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                                        <Typography variant="caption">{r.text}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {r.date ? new Date(r.date).toLocaleDateString('en-GB') : ''}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : typeof currentInfluencerRemarks === 'string' && currentInfluencerRemarks ? (
                            <Box sx={{ mt: 2, mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Previous Remarks:</Typography>
                                <Typography variant="caption" sx={{ display: 'block', py: 0.5 }}>{currentInfluencerRemarks}</Typography>
                            </Box>
                        ) : null
                    )} */}

                    <TextField
                        fullWidth
                        label="Add Remark"
                        multiline
                        rows={3}
                        value={statusRemark}
                        onChange={(e) => setStatusRemark(e.target.value)}
                        placeholder="Enter new remark..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleStatusModalUpdate} variant="contained">
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={remarksModalOpen} onClose={() => setRemarksModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Remarks - {remarksModalName}</DialogTitle>
                <DialogContent>
                    {remarksModalData.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                            {remarksModalData.map((r, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                                    <Typography variant="body2">{r.text}</Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                                        {r.date ? new Date(r.date).toLocaleDateString('en-GB') : ''}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary">No remarks found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemarksModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminInfluencers;
